<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div class="text-center mb-2.5">
                <h1 class="font-header text-[3rem] xl:text-[3rem] font-bold text-primary mb-1.5">
                    Submit Your Manuscript
                </h1>
                <div class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
                    v-html="pageDescription || 'Always looking for new voices and compelling stories.'">
                </div>
            </div>

            <div class="bg-white border border-gray-300 rounded-lg p-2.5 xl:p-4 mb-2.5 shadow-sm">
                <div v-if="guidelinesText"
                    class="font-body text-[1.125rem] text-gray-700 leading-[1.75] guidelines-container mb-3 content-page-section"
                    v-html="guidelinesText"></div>

                <ul v-if="guidelinesFileUrl || guidelinesFileUrlFr"
                    class="list-disc list-inside font-body text-[1.125rem] text-gray-700 leading-[1.75] space-y-1">
                    <li v-if="guidelinesFileUrl">
                        <a :href="guidelinesFileUrl" target="_blank" rel="noopener noreferrer"
                            class="text-green-700 hover:underline">
                            Our Guidelines (English)
                        </a>
                    </li>
                    <li v-if="guidelinesFileUrlFr">
                        <a :href="guidelinesFileUrlFr" target="_blank" rel="noopener noreferrer"
                            class="text-green-700 hover:underline">
                            Our Guidelines (Français)
                        </a>
                    </li>
                </ul>
            </div>

            <div v-if="!isAuthenticated" class="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 xl:p-4 mb-2.5">
                <div class="flex items-start">
                    <svg class="w-8 h-8 text-yellow-600 mr-4 flex-shrink-0 mt-1" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div class="flex-1">
                        <h2 class="font-header text-[1.5rem] font-bold text-yellow-800 mb-1">
                            Login Required
                        </h2>
                        <p class="font-body text-[1.125rem] text-gray-700 leading-[1.75] mb-1">
                            You must be logged in to submit a manuscript. Please log in or create an account to
                            continue.
                        </p>
                        <div class="flex gap-4">
                            <a href="/login"
                                class="inline-block bg-green-700 text-white px-6 py-2 rounded-md font-header font-semibold hover:bg-green-800 transition-colors">
                                Log In
                            </a>
                            <a href="/register"
                                class="inline-block bg-white text-green-700 border border-green-700 px-6 py-2 rounded-md font-header font-semibold hover:bg-green-50 transition-colors">
                                Create Account
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="submissionSuccess"
                class="bg-green-50 border border-green-200 rounded-lg p-2.5 xl:p-4 mb-2.5">
                <div class="flex items-start">
                    <svg class="w-8 h-8 text-green-600 mr-4 flex-shrink-0 mt-1" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                        <h2 class="font-header text-[1.5rem] font-bold text-green-800 mb-1">
                            Thank You for Your Submission!
                        </h2>
                        <p class="font-body text-[1.125rem] text-gray-700 leading-[1.75]">
                            Our team will review your manuscript and will be in touch with you soon. We appreciate your
                            patience.
                        </p>
                    </div>
                </div>
            </div>

            <div v-else class="bg-white rounded-lg shadow-sm p-2.5 xl:p-4">
                <form @submit.prevent="handleSubmit" class="space-y-1.5">
                    <div>
                        <label for="title" class="block text-sm font-header font-semibold text-primary mb-1">
                            Manuscript Title <span class="text-red-500">*</span>
                        </label>
                        <input type="text" id="title" v-model="form.title" required
                            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                            placeholder="Enter your manuscript title" />
                    </div>

                    <div class="grid grid-cols-1 xl:grid-cols-2 gap-1.5">
                        <div>
                            <label for="firstName" class="block text-sm font-header font-semibold text-primary mb-1">
                                First Name <span class="text-red-500">*</span>
                            </label>
                            <input type="text" id="firstName" v-model="form.firstName" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                placeholder="Your first name" />
                        </div>

                        <div>
                            <label for="lastName" class="block text-sm font-header font-semibold text-primary mb-1">
                                Last Name <span class="text-red-500">*</span>
                            </label>
                            <input type="text" id="lastName" v-model="form.lastName" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                placeholder="Your last name" />
                        </div>
                    </div>

                    <div>
                        <label for="email" class="block text-sm font-header font-semibold text-primary mb-1">
                            Email <span class="text-red-500">*</span>
                        </label>
                        <input type="email" id="email" v-model="form.email" required
                            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                            placeholder="your.email@example.com" />
                    </div>

                    <div>
                        <label for="genre" class="block text-sm font-header font-semibold text-primary mb-1">
                            Genre <span class="text-red-500">*</span>
                        </label>
                        <select id="genre" v-model="form.genre" required
                            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]">
                            <option value="">Select a genre</option>
                            <option value="fiction">Fiction</option>
                            <option value="non-fiction">Non-Fiction</option>
                            <option value="mystery">Mystery</option>
                            <option value="romance">Romance</option>
                            <option value="sci-fi">Science Fiction</option>
                            <option value="fantasy">Fantasy</option>
                            <option value="historical">Historical</option>
                            <option value="biography">Biography</option>
                            <option value="memoir">Memoir</option>
                            <option value="poetry">Poetry</option>
                            <option value="children">Children's</option>
                            <option value="young-adult">Young Adult</option>
                            <option value="other">Other</option>
                        </select>
                        <div v-if="form.genre === 'other'" class="mt-1">
                            <label for="customGenre" class="block text-sm font-header font-semibold text-primary mb-1">
                                Custom Genre <span class="text-red-500">*</span>
                            </label>
                            <input type="text" id="customGenre" v-model="form.customGenre" required
                                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                placeholder="Please specify your genre" />
                        </div>
                    </div>

                    <div>
                        <label for="manuscriptFile" class="block text-sm font-header font-semibold text-primary mb-1">
                            Manuscript Sample <span class="text-red-500">*</span>
                        </label>
                        <input type="file" id="manuscriptFile" @change="handleManuscriptFileSelect"
                            accept=".pdf,.doc,.docx" required
                            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                        <p class="text-xs text-gray-500 mt-1 font-body">
                            Accepted formats: PDF, DOC, DOCX (Max 10MB)
                        </p>
                        <p v-if="form.fileName" class="font-body text-[1.125rem] text-gray-700 mt-1 leading-[1.75]">
                            Selected: {{ form.fileName }}
                        </p>
                    </div>

                    <div>
                        <label for="letterOfProposal" class="block text-sm font-header font-semibold text-primary mb-1">
                            Letter of Proposal <span class="text-red-500">*</span>
                        </label>
                        <input type="file" id="letterOfProposal" @change="handleLetterFileSelect"
                            accept=".pdf,.doc,.docx" required
                            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                        <p class="text-xs text-gray-500 mt-1 font-body">
                            Accepted formats: PDF, DOC, DOCX (Max 10MB)
                        </p>
                        <p v-if="form.letterFileName"
                            class="font-body text-[1.125rem] text-gray-700 mt-1 leading-[1.75]">
                            Selected: {{ form.letterFileName }}
                        </p>
                    </div>

                    <div>
                        <label for="additionalInfo" class="block text-sm font-header font-semibold text-primary mb-1">
                            Additional Information
                        </label>
                        <textarea id="additionalInfo" v-model="form.additionalInfo" rows="4"
                            class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75] resize-none"
                            placeholder="Any additional information you would like to share (publication history, writing experience, etc.)"></textarea>
                    </div>

                    <button type="submit" :disabled="isSubmitting"
                        class="w-full bg-accent text-white py-3 px-6 rounded-md font-header font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ isSubmitting ? 'Submitting...' : 'Submit Manuscript' }}
                    </button>
                    <p v-if="submitError" class="text-red-600 font-body text-[1.125rem] text-center leading-[1.75]">
                        {{ submitError }}
                    </p>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { submissionService } from '../services/submission'
import { authService } from '../services/auth'
import { publicService } from '../services/public'

const isAuthenticated = ref(false)
const currentUser = ref(null)
const pageDescription = ref('')
const guidelinesText = ref('')
const guidelinesFileUrl = ref('')
const guidelinesFileUrlFr = ref('')

const form = ref({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    genre: '',
    customGenre: '',
    fileName: '',
    file: null,
    letterFileName: '',
    letterFile: null,
    additionalInfo: ''
})

watch(() => form.value.genre, (newGenre) => {
    if (newGenre !== 'other') {
        form.value.customGenre = ''
    }
})

const isSubmitting = ref(false)
const submissionSuccess = ref(false)
const submitError = ref('')

const loadPageDescription = async () => {
    try {
        const page = await publicService.getPage('Submissions')
        if (page) {
            if (page.description) {
                pageDescription.value = page.description
            }
            if (page.guidelines_text) {
                guidelinesText.value = page.guidelines_text
            }
            if (page.guidelines_file_url) {
                guidelinesFileUrl.value = page.guidelines_file_url
            }
            if (page.guidelines_file_url_fr) {
                guidelinesFileUrlFr.value = page.guidelines_file_url_fr
            }
        }
    } catch (error) {
        console.error('Failed to load page description:', error)
    }
}

onMounted(async () => {
    const user = await authService.getCurrentUser()
    if (user) {
        isAuthenticated.value = true
        currentUser.value = user
        form.value.email = user.email || ''
    }
    loadPageDescription()
})

const handleManuscriptFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        if (file.size > 10 * 1024 * 1024) {
            alert('File size must be less than 10MB')
            event.target.value = ''
            return
        }
        form.value.fileName = file.name
        form.value.file = file
    }
}

const handleLetterFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        if (file.size > 10 * 1024 * 1024) {
            alert('File size must be less than 10MB')
            event.target.value = ''
            return
        }
        form.value.letterFileName = file.name
        form.value.letterFile = file
    }
}

const handleSubmit = async () => {
    if (!isAuthenticated.value) {
        submitError.value = 'Please log in to submit a manuscript.'
        return
    }

    isSubmitting.value = true
    submitError.value = ''
    submissionSuccess.value = false

    try {
        if (!form.value.file) {
            submitError.value = 'Please select a manuscript sample file.'
            isSubmitting.value = false
            return
        }

        if (!form.value.letterFile) {
            submitError.value = 'Please select a letter of proposal file.'
            isSubmitting.value = false
            return
        }

        if (form.value.genre === 'other' && !form.value.customGenre?.trim()) {
            submitError.value = 'Please specify your custom genre.'
            isSubmitting.value = false
            return
        }

        const uploadResult = await submissionService.uploadManuscriptFile(form.value.file)
        if (!uploadResult.success) {
            submitError.value = uploadResult.error || 'Failed to upload manuscript sample.'
            isSubmitting.value = false
            return
        }
        const manuscriptFileUrl = uploadResult.url

        const letterUploadResult = await submissionService.uploadManuscriptFile(form.value.letterFile)
        if (!letterUploadResult.success) {
            submitError.value = letterUploadResult.error || 'Failed to upload letter of proposal.'
            isSubmitting.value = false
            return
        }
        const letterOfProposalUrl = letterUploadResult.url

        const submissionData = {
            first_name: form.value.firstName.trim(),
            last_name: form.value.lastName.trim(),
            email: form.value.email,
            title: form.value.title,
            genre: form.value.genre === 'other' ? form.value.customGenre.trim() : form.value.genre,
            manuscript_file_url: manuscriptFileUrl,
            letter_of_proposal_url: letterOfProposalUrl
        }

        const result = await submissionService.submitManuscript(submissionData)

        if (result.success) {
            submissionSuccess.value = true
            form.value = {
                title: '',
                firstName: '',
                lastName: '',
                email: currentUser.value?.email || '',
                genre: '',
                customGenre: '',
                fileName: '',
                file: null,
                letterFileName: '',
                letterFile: null,
                additionalInfo: ''
            }
            const manuscriptFileInput = document.getElementById('manuscriptFile')
            if (manuscriptFileInput) {
                manuscriptFileInput.value = ''
            }
            const letterFileInput = document.getElementById('letterOfProposal')
            if (letterFileInput) {
                letterFileInput.value = ''
            }
            window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
            submitError.value = result.error || 'Sorry, there was an error submitting your manuscript. Please try again.'
        }
    } catch (error) {
        console.error('Error submitting manuscript:', error)
        if (error.response?.status === 401) {
            submitError.value = 'Your session has expired. Please log in again.'
            isAuthenticated.value = false
        } else {
            submitError.value = 'Sorry, there was an error submitting your manuscript. Please try again.'
        }
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
.guidelines-container :deep(.guidelines-link) {
    color: #2D4D31;
    text-decoration: underline;
    font-weight: 600;
    transition: color 0.2s ease;
}

.guidelines-container :deep(.guidelines-link:hover) {
    color: #1a3320;
    text-decoration: underline;
}

.guidelines-link {
    color: #2D4D31;
    text-decoration: underline;
    font-weight: 600;
    transition: color 0.2s ease;
}

.guidelines-link:hover {
    color: #1a3320;
    text-decoration: underline;
}
</style>
